import { homApi } from '~/redux/apis/hom'
import {
  SubtopicTimesResponse,
  UserTimeParams,
  UserStatsSummaryArgs,
  UserStatsSummaryResponse,
  UserTimeResponse,
  UserTopicStatsArgs,
  UserTopicStatsResponse,
} from '~/statistics-v2/types/statistics.types'

export const curriculumProgressApi = homApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * The panel's first paint: one grade cut at topic level, or `overall` only for All grades. The full
     * all-grades tree was ~3.4 MB and the panel rendered one grade of it.
     *
     * `params`, not a hand-built URL, so an absent `gradeId`/`userId` is dropped rather than sent empty.
     *
     * `keepUnusedDataFor: 300` (the global default is 1s) keeps each grade's answer cached, so switching
     * back to a grade, or the V1→V2 flag swap remounting the page, has `currentData` straight away and
     * shows numbers instead of a skeleton. It does NOT save the request: `homApi`'s global
     * `refetchOnMountOrArgChange: 5` still refetches in the background once the entry is over 5s old.
     */
    getUserStatsSummary: builder.query<UserStatsSummaryResponse, UserStatsSummaryArgs>({
      query: ({ countryCode, gradeId, userId }) => ({
        url: '/v1/progress/get-user-stats',
        method: 'GET',
        params: { countryCode, scope: 'summary', gradeId, userId },
      }),
      keepUnusedDataFor: 300,
      providesTags: ['userStats'],
    }),
    /** One topic's subtopics and level details, for the topic drill-down ("See More"). */
    getUserTopicStats: builder.query<UserTopicStatsResponse, UserTopicStatsArgs>({
      query: ({ countryCode, gradeId, topicId, userId }) => ({
        url: '/v1/progress/get-user-topic-stats',
        method: 'GET',
        params: { countryCode, gradeId, topicId, userId },
      }),
      keepUnusedDataFor: 300,
      providesTags: ['userStats'],
    }),
    getUserTime: builder.query<UserTimeResponse, UserTimeParams & { userId?: number }>({
      query: ({ countryCode, gradeId, topicOrder, subtopicLevel, userId }) => {
        const params = new URLSearchParams({ countryCode })
        if (gradeId) params.append('gradeId', gradeId)
        if (topicOrder !== undefined) params.append('topicOrder', String(topicOrder))
        if (subtopicLevel) params.append('subtopicLevel', subtopicLevel)
        if (userId) params.append('userId', String(userId))
        return { url: `/v1/progress/get-user-time?${params.toString()}`, method: 'GET' }
      },
      providesTags: ['userStats'],
    }),
    /**
     * Every subtopic's time at once, for the subtopic cards in the topic drill-down.
     *
     * ⚠ Deliberately NOT part of `getUserStats`: hom-api #1935 moved time out of that payload because
     * computing it per node made the panel slow. This is the batched replacement — one request for the
     * whole curriculum, not one per subtopic, so expanding subtopics costs nothing extra.
     *
     * Keys carry the country and grade, so a single response serves every grade the panel switches to.
     */
    getUserSubtopicTimes: builder.query<SubtopicTimesResponse, { userId?: number }>({
      query: ({ userId }) => {
        const params = new URLSearchParams()
        if (userId) params.append('userId', String(userId))
        const query = params.toString()
        return { url: `/v1/progress/get-user-subtopic-times${query ? `?${query}` : ''}`, method: 'GET' }
      },
      providesTags: ['userStats'],
    }),
    getUserRecentCurriculumCountry: builder.query<{ countryCode: string }, { familyMemberId?: number }>({
      query: ({ familyMemberId }) => ({
        url: `/v1/progress/get-recent-curriculum${familyMemberId ? `?familyMemberId=${familyMemberId}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['userStats'],
    }),
    getDashboardStats: builder.query<
      { timeSpent: number; activities: number; accuracy: number; videoWatchTime: number },
      void
    >({
      query: () => ({ url: '/v1/progress/get-dashboard-stats', method: 'GET' }),
      providesTags: ['userStats'],
    }),
  }),
})

export const {
  useGetUserStatsSummaryQuery,
  useGetUserTopicStatsQuery,
  useGetUserSubtopicTimesQuery,
  useGetUserTimeQuery,
  useGetUserRecentCurriculumCountryQuery,
  useGetDashboardStatsQuery,
} = curriculumProgressApi
